import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAlertDialog } from '../contexts/AlertDialogContext';
import { useTranslation } from 'react-i18next';
import {
    FaEnvelopeOpenText,
    FaUsers,
    FaSignOutAlt,
    FaListAlt,
    FaToggleOn,
    FaChartBar,
    FaLink,
    FaTimes,
    FaClock,
    FaHistory,
    FaChartLine,
    FaTrash,
    FaChartPie,
    FaUserCheck,
    FaWrench,
    FaEnvelope
} from "react-icons/fa";
import './Dashboard.css';

// Import des composants séparés
import RegisteredUsersTable from '../components/Dashboard/RegisteredUsersTable';
import GestionnaireLiens from '../components/Dashboard/GestionnaireLiens'; // NEW IMPORT
import ClickHistory from '../components/Dashboard/ClickHistory';
import LinkPerformanceSummary from '../components/Dashboard/LinkPerformanceSummary';
import GeneralStats from '../components/Dashboard/GeneralStats';
import EmailModal from '../components/Dashboard/EmailModal';
import MessageForm from '../components/Dashboard/MessageForm';
import InvitationManager from '../components/Dashboard/InvitationManager';
import RegistrationList from '../components/Dashboard/RegistrationList';
import InvitationStats from '../components/Dashboard/InvitationStats';
import RegistrationHistory from '../components/Dashboard/RegistrationHistory';
import AlertDialog from '../components/common/AlertDialog';
import EditSessionModal from '../components/Dashboard/EditSessionModal';
import LoadingSpinner from '../components/common/LoadingSpinner';
import SessionHistoryTable from '../components/Dashboard/SessionHistoryTable';
import DeletedLinksTable from '../components/Dashboard/DeletedLinksTable';

const API_URL = "https://back-desalpe.onrender.com/api"; // URL de base pour l'API

const Dashboard = () => {
  const { openAlertDialog } = useAlertDialog();
  const { i18n } = useTranslation();
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeView, setActiveView] = useState('invitation');
  const [invitationView, setInvitationView] = useState('manage');
  const [inscriptionView, setInscriptionView] = useState('list');
  const [linkView, setLinkView] = useState('default');
  const [clickStatsView, setClickStatsView] = useState('general');
  const [selectedSessionFilter, setSelectedSessionFilter] = useState('');
  
  const [invitations, setInvitations] = useState([]);
  const [registeredUsers, setRegisteredUsers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sessionRegistrationCounts, setSessionRegistrationCounts] = useState({});
  const [allTransformedUsers, setAllTransformedUsers] = useState([]);
  const [marketingLinks, setMarketingLinks] = useState([]);
  const [prospects, setProspects] = useState([]);

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [ambassadorEmailLists, setAmbassadorEmailLists] = useState({ default: [], custom: [] });
  const [showTargetedEmailModal, setShowTargetedEmailModal] = useState(false);
  const [selectedCityForEmail, setSelectedCityForEmail] = useState('');
  const [selectedSessionTitleForEmail, setSelectedSessionTitleForEmail] = useState('');
  const navigate = useNavigate();

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const headers = { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}` 
      };

      const responses = await Promise.all([
        fetch(`${API_URL}/invitations`, { headers }),
        fetch(`${API_URL}/countries`, { headers }),
        fetch(`${API_URL}/cities`, { headers }),
        fetch(`${API_URL}/sessions`, { headers }),
        fetch(`${API_URL}/inscription/liens`, { headers }),
        fetch(`${API_URL}/inscription/prospects`, { headers }),
      ]);

      for (const res of responses) {
        if (res.status === 401 || res.status === 403) throw new Error('Authentication failed');
        if (!res.ok) throw new Error(`Failed to fetch with status: ${res.status}`);
      }

      const [invitationsData, countriesData, citiesData, sessionsData, linksData, prospectsData] = await Promise.all(
        responses.map(res => res.json())
      );

      setInvitations(invitationsData);
      setMarketingLinks(linksData);
      setProspects(prospectsData);
      
      const transformedCountries = countriesData.map(country => ({
          ...country,
          name: {
              fr: country.name_fr,
              en: country.name_en
          }
      }));
      setCountries(transformedCountries);

      const transformedCities = citiesData.map(city => ({
          ...city,
          countryId: city.country_id,
          name: {
              fr: city.name_fr,
              en: city.name_en
          }
      }));
      setCities(transformedCities);
      
      const convertedSessions = sessionsData.map(session => ({
        ...session,
        cityId: session.city_id,
        startTime: session.start_time,
        endTime: session.end_time,
      }));
      setSessions(convertedSessions);

      const transformedRegisteredUsers = invitationsData.map(invitation => {
        const country = countriesData.find(c => c.id === invitation.country_id);
        const city = citiesData.find(c => c.id === invitation.city_id);
        const userSessions = (invitation.sessions || []).map(invitationSession => {
            const fullSession = convertedSessions.find(s => s.id === invitationSession.id);
            return fullSession || invitationSession;
        });
        return {
          id: invitation.id,
          firstName: invitation.first_name,
          lastName: invitation.last_name,
          email: invitation.email,
          phoneNumber: invitation.phone_number,
          registrationDate: invitation.created_at,
          countryName: country ? (country.name_fr || country.name_en) : 'N/A',
          cityName: city ? (city.name_fr || city.name_en) : 'N/A',
          sessions: userSessions,
          sessionIds: userSessions.map(s => s.id),
          isDeleted: invitation.is_deleted || false,
        };
      });
      setAllTransformedUsers(transformedRegisteredUsers);

      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);

      const filteredRegisteredUsers = transformedRegisteredUsers.filter(user => {
        const shouldIncludeUser = user.sessions.some(session => {
          if (!session.start_time) return false;
          return new Date(session.start_time) >= todayStart;
        });
        return shouldIncludeUser;
      });

      setRegisteredUsers(filteredRegisteredUsers);

      const counts = {};
      convertedSessions.forEach(session => {
        counts[session.id] = {
          ...session,
          totalCount: 0,
        };
      });

      invitationsData.forEach(invitation => {
        if (invitation.sessions && Array.isArray(invitation.sessions)) {
          invitation.sessions.forEach(session => {
            if (counts[session.id]) {
              counts[session.id].totalCount++;
            }
          });
        }
      });
      setSessionRegistrationCounts(counts);

    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
      if (error.message === 'Authentication failed') {
          navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      setIsAdmin(true);
      fetchAllData();
    }
    else {
      navigate('/login');
    }
  }, [navigate, fetchAllData]);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    navigate('/login');
  };

  const apiPost = async (endpoint, body) => {
    const token = localStorage.getItem('authToken');
    await fetch(`${API_URL}/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body),
    });
    fetchAllData();
  };

  const apiPut = async (endpoint, id, body) => {
    const token = localStorage.getItem('authToken');
    try {
      const url = id ? `${API_URL}/${endpoint}/${id}` : `${API_URL}/${endpoint}`;
      const response = await fetch(url, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(body),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `API error: ${response.status}`);
      }
    } catch (error) {
      console.error(`apiPut: Fetch error for ${endpoint}/${id}:`, error);
      throw error;
    } finally {
      fetchAllData();
    }
  };

  const apiDelete = async (endpoint, id) => {
    openAlertDialog('Êtes-vous sûr de vouloir supprimer cet élément ?', 'Confirmation de Suppression', async () => {
      const token = localStorage.getItem('authToken');
      try {
        const response = await fetch(`${API_URL}/${endpoint}/${id}`, { 
            method: 'DELETE', 
            headers: { 'Authorization': `Bearer ${token}` } 
        });
        if (!response.ok) {
            const errorData = await response.json();
            openAlertDialog(errorData.message || 'Une erreur est survenue lors de la suppression.', 'Erreur de Suppression');
        } else {
            openAlertDialog('Élément supprimé avec succès !', 'Succès');
        }
        fetchAllData();
      } catch (error) {
        console.error("Erreur lors de la suppression:", error);
        openAlertDialog('Une erreur est survenue lors de la suppression.', 'Erreur');
      }
    });
  };

  const handleSendEmailToUser = (userEmail) => {
      setEmailRecipients([userEmail]);
      setShowEmailModal(true);
  };

  const handleSendGroupEmail = (recipientList) => {
      setEmailRecipients(recipientList);
      setShowEmailModal(true);
  };

  const handleOpenGeneralMessageModal = () => {
    const defaultEmails = marketingLinks
      .filter(link => link.type_lien === 'defaut' && link.ambassadeur_email)
      .map(link => link.ambassadeur_email);
    
    const customEmails = marketingLinks
      .filter(link => link.type_lien === 'personnalise' && link.ambassadeur_email)
      .map(link => link.ambassadeur_email);

    setAmbassadorEmailLists({ default: defaultEmails, custom: customEmails });
    setShowEmailModal(true);
  };

  const handleSendGeneralEmail = async (subject, body, recipients = []) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      openAlertDialog('Vous n\'êtes pas authentifié pour envoyer des emails.', 'Erreur d\'authentification');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/email/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          to: recipients.length > 0 ? recipients.join(', ') : emailRecipients.join(', '),
          subject: subject,
          html: body
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        openAlertDialog(errorData.message || 'Une erreur est survenue lors de l\'envoi de l\'email.', 'Erreur d\'envoi d\'Email');
      } else {
        openAlertDialog('Email envoyé avec succès !', 'Succès');
        setShowEmailModal(false);
        setEmailRecipients([]);
      }
    } catch (error) {
      console.error("Fetch error sending email:", error);
      openAlertDialog('Une erreur réseau est survenue lors de l\'envoi de l\'email.', 'Erreur Réseau');
    }
  };

  const handleCreateLink = async (newLinkData) => {
    try {
      await apiPost('inscription/liens', newLinkData);
      openAlertDialog('Lien créé avec succès !', 'Succès');
    } catch (error) {
      console.error('Erreur lors de la création du lien:', error);
      openAlertDialog('Une erreur est survenue lors de la création du lien.', 'Erreur');
    }
  };

  const handleUpdateLink = async (linkId, updatedData) => {
    try {
      await apiPut('inscription/liens', linkId, updatedData);
      openAlertDialog('Lien mis à jour avec succès !', 'Succès');
    } catch (error) {
      console.error('Erreur lors de la mise à jour du lien:', error);
      openAlertDialog('Une erreur est survenue lors de la mise à jour du lien.', 'Erreur');
    }
  };

  const handleDeleteLink = async (linkId) => {
    openAlertDialog('Êtes-vous sûr de vouloir supprimer cet élément ?', 'Confirmation de Suppression', async () => {
      const token = localStorage.getItem('authToken');
      try {
        const response = await fetch(`${API_URL}/inscription/liens/${linkId}`, { 
            method: 'DELETE', 
            headers: { 'Authorization': `Bearer ${token}` } 
        });
        if (!response.ok) {
            const errorData = await response.json();
            openAlertDialog(errorData.message || 'Une erreur est survenue lors de la suppression.', 'Erreur de Suppression');
        } else {
            openAlertDialog('Élément supprimé avec succès !', 'Succès');
        }
        fetchAllData();
      } catch (error) {
        console.error("Erreur lors de la suppression:", error);
        openAlertDialog('Une erreur est survenue lors de la suppression.', 'Erreur');
      }
    });
  };

  const [showEditSessionModal, setShowEditSessionModal] = useState(false);
  const [sessionToEdit, setSessionToEdit] = useState(null);

  const handleEndSession = async (sessionToUpdate) => {
    openAlertDialog('Êtes-vous sûr de vouloir terminer cette session ?', 'Confirmer la fin de session', async () => {
      try {
        await apiPut('sessions', sessionToUpdate.id, { is_terminated: true });
        openAlertDialog('Session terminée avec succès !', 'Succès');
      } catch (error) {
        console.error('handleEndSession: Erreur lors de la fin de session:', error);
        openAlertDialog('Une erreur est survenue lors de la fin de session.', 'Erreur');
      }
    });
  };

  const handleRelaunchSession = async (sessionToRelaunch) => {
    openAlertDialog('Êtes-vous sûr de vouloir relancer cette session ?', 'Confirmer la relance', async () => {
      try {
        await apiPut('sessions', sessionToRelaunch.id, {
          is_terminated: false,
        });
        openAlertDialog('Session relancée avec succès !', 'Succès');
      } catch (error) {
        console.error('handleRelaunchSession: Erreur lors de la relance de session:', error);
        openAlertDialog('Une erreur est survenue lors de la relance de session.', 'Erreur');
      }
    });
  };

  const handleEditSession = (session) => {
    setSessionToEdit(session);
    setShowEditSessionModal(true);
  };

  const handleSaveEditedSession = async (editedSession) => {
    try {
      const transformedSession = {
        ...editedSession,
        city_id: editedSession.city_id,
        start_time: editedSession.startTime,
        end_time: editedSession.endTime,
      };
      delete transformedSession.startTime;
      delete transformedSession.endTime;
      await apiPut('sessions', transformedSession.id, transformedSession);
      openAlertDialog('Session modifiée avec succès !', 'Succès');
      setShowEditSessionModal(false);
      setSessionToEdit(null);
    } catch (error) {
      console.error('Erreur lors de la modification de session:', error);
      openAlertDialog('Une erreur est survenue lors de la modification de session.', 'Erreur');
    }
  };

  const handleRestoreLink = async (linkId, valideJusquA = null) => {
    try {
      const body = valideJusquA !== null ? { valide_jusqu_a: valideJusquA } : {};
      await apiPut(`inscription/liens/${linkId}/restore`, null, body);
      openAlertDialog('Lien restauré avec succès !', 'Succès');
    } catch (error) {
      console.error('Erreur lors de la restauration du lien:', error);
      openAlertDialog('Une erreur est survenue lors de la restauration du lien.', 'Erreur');
    }
  };

  const handleSendReport = async (link) => {
    try {
      await fetch(`${API_URL}/inscription/liens/${link.id}/send-report`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      });
      openAlertDialog('Rapport envoyé avec succès !', 'Succès');
    } catch (error) {
      console.error('Erreur lors de l\'envoi du rapport:', error);
      openAlertDialog('Une erreur est survenue lors de l\'envoi du rapport.', 'Erreur');
    }
  };

  const handleSendAllReports = async (type) => {
    const linksToSendReports = marketingLinks.filter(link => link.type_lien === type && !link.est_supprime);

    if (linksToSendReports.length === 0) {
      openAlertDialog(`Aucun lien ${type === 'defaut' ? 'par défaut' : 'personnalisé'} actif trouvé pour envoyer des rapports.`, 'Information');
      return;
    }

    openAlertDialog(`Envoi de ${linksToSendReports.length} rapports ${type === 'defaut' ? 'par défaut' : 'personnalisés'} en cours...`, 'Envoi en cours');

    for (const link of linksToSendReports) {
      try {
        await handleSendReport(link);
      } catch (error) {
        console.error(`Erreur lors de l\'envoi du rapport pour le lien ${link.id}:`, error);
        // Continue to send other reports even if one fails
      }
    }
    openAlertDialog('Tous les rapports ont été traités.', 'Succès');
  };

  if (!isAdmin) {
    return <div>Chargement ou non autorisé...</div>;
  }

  const renderInscriptionContent = () => {
      const defaultLink = marketingLinks.find(link => link.type_lien === 'defaut');
      const customLinks = marketingLinks.filter(link => link.type_lien === 'personnalise');

      return (
        <div>
          <h2>Gestion des inscriptions</h2>
          <nav className="sub-nav">
            <div onClick={() => setInscriptionView("list")} className={inscriptionView === "list" ? "active" : ""}>
              <FaListAlt /> Liste des Inscrits
            </div>
            <div onClick={() => setInscriptionView("links")} className={inscriptionView === "links" ? "active" : ""}>
              <FaLink /> Gestion des Liens
            </div>
            <div onClick={() => setInscriptionView("clicks")} className={inscriptionView === "clicks" ? "active" : ""}>
              <FaChartLine /> Suivi des Clics
            </div>
          </nav>
          <div className="sub-content">
            {(() => {
              switch (inscriptionView) {
                case 'list':
                  return <RegistrationList 
                      prospects={prospects} 
                      onSendEmail={handleSendEmailToUser}
                      onSendGroupEmail={handleSendGroupEmail}
                  />;
                case 'links':
                  return (
                    <div>
                      <nav className="tertiary-nav">
                          <button onClick={() => setLinkView('default')} className={linkView === 'default' ? 'active' : ''}>
                              Gestion Lien par Défaut
                          </button>
                          <button onClick={() => setLinkView('custom')} className={linkView === 'custom' ? 'active' : ''}>
                              Gestion Liens Personnalisés
                          </button>
                          <button onClick={() => setLinkView('deleted')} className={linkView === 'deleted' ? 'active' : ''}>
                              Liens Supprimés
                          </button>
                      </nav>
                      {linkView === 'default' && <GestionnaireLiens 
                          links={marketingLinks.filter(link => !link.est_supprime)} 
                          type="defaut" 
                          onCreate={handleCreateLink} 
                          onUpdate={handleUpdateLink} 
                          onDelete={handleDeleteLink} 
                          onSendReport={handleSendReport}
                          onSendAllReports={handleSendAllReports}
                      />}
                      {linkView === 'custom' && <GestionnaireLiens 
                          links={marketingLinks.filter(link => !link.est_supprime)} 
                          type="personnalise" 
                          onCreate={handleCreateLink} 
                          onUpdate={handleUpdateLink} 
                          onDelete={handleDeleteLink} 
                          onSendReport={handleSendReport}
                          onSendAllReports={handleSendAllReports}
                      />}
                      {linkView === 'deleted' && <DeletedLinksTable 
                          links={marketingLinks.filter(link => link.est_supprime)} 
                          onRestoreLink={handleRestoreLink} 
                          onSendReport={handleSendReport} 
                      />}
                    </div>
                  );
                case 'clicks':
                  return (
                    <div>
                      <nav className="tertiary-nav">
                        <button onClick={() => setClickStatsView('general')} className={clickStatsView === 'general' ? 'active' : ''}>
                          Statistiques Générales
                        </button>
                        <button onClick={() => setClickStatsView('performance')} className={clickStatsView === 'performance' ? 'active' : ''}>
                          Résumé des Performances
                        </button>
                        <button onClick={() => setClickStatsView('history')} className={clickStatsView === 'history' ? 'active' : ''}>
                          Historique des Clics
                        </button>
                      </nav>
                      <div className="sub-content">
                        {clickStatsView === 'general' && <GeneralStats />}
                        {clickStatsView === 'performance' && <LinkPerformanceSummary onSendGeneralEmail={handleOpenGeneralMessageModal} />}
                        {clickStatsView === 'history' && <ClickHistory />}
                      </div>
                    </div>
                  );
                default:
                  return null;
              }
            })()}
          </div>
        </div>
    )};

  const renderInvitationSection = () => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const completedSessions = sessions.filter(s => s.startTime && new Date(s.startTime) < todayStart && !s.is_terminated);
    const completedSessionsCount = completedSessions.length;

    return (
      <div>
        <h2>Gestion des invitations</h2>
        <nav className="sub-nav">
          <div onClick={() => setInvitationView("manage")} className={invitationView === "manage" ? "active" : ""}>
            <FaWrench /> Gérer les sessions
          </div>
          <div onClick={() => setInvitationView("list")} className={invitationView === "list" ? "active" : ""}>
            <FaUserCheck /> Liste des Inscrits
          </div>
          <div onClick={() => setInvitationView("stats")} className={invitationView === "stats" ? "active" : ""}>
            <FaChartBar /> Statistiques
          </div>
          <div onClick={() => setInvitationView("history")} className={invitationView === "history" ? "active" : ""}>
            <FaHistory /> Historique des Inscriptions
          </div>
          <div onClick={() => setInvitationView("sessionHistory")} className={invitationView === "sessionHistory" ? "active" : ""}>
            <FaClock /> Historique des Sessions
          </div>
        </nav>
        <div className="sub-content">
          {(() => {
            switch (invitationView) {
              case 'manage':
                return <InvitationManager
                  countries={countries}
                  cities={cities}
                  sessions={sessions}
                  onCountryAdd={(data) => apiPost('countries', data)}
                  onCityAdd={(data) => apiPost('cities', data)}
                  onSessionAdd={(data) => apiPost('sessions', data)}
                  onCountryDelete={(id) => apiDelete('countries', id)}
                  onCityDelete={(id) => apiDelete('cities', id)}
                  onSessionDelete={(id) => apiDelete('sessions', id)}
                  onEndSession={handleEndSession}
                  onEditSession={handleEditSession}
                  onRelaunchSession={handleRelaunchSession} // Pass new handler
                />;
              case 'list':
                const sessionsToDisplay = selectedSessionFilter
                  ? sessions.filter(s => s.title === selectedSessionFilter && new Date(s.startTime) >= todayStart && !s.is_terminated)
                  : sessions.filter(s => new Date(s.startTime) >= todayStart && !s.is_terminated);

                const uniqueSessionTitles = [...new Set(sessions.map(session => session.title))];

                const filteredCountsForList = {};
                sessionsToDisplay.forEach(session => {
                  if (sessionRegistrationCounts[session.id]) {
                    filteredCountsForList[session.id] = sessionRegistrationCounts[session.id];
                  }
                });

                return (
                  <div>
                    <div className="tertiary-nav">
                      <button
                        onClick={() => setSelectedSessionFilter('')}
                        className={selectedSessionFilter === '' ? 'active' : ''}
                      >
                        Toutes les sessions
                      </button>
                      {uniqueSessionTitles.map(sessionTitle => (
                        <button
                          key={sessionTitle}
                          onClick={() => setSelectedSessionFilter(sessionTitle)}
                          className={selectedSessionFilter === sessionTitle ? 'active' : ''}
                        >
                          {sessionTitle}
                        </button>
                      ))}
                    </div>
                    <RegisteredUsersTable
                      users={registeredUsers.filter(user => !user.isDeleted)}
                      sessions={sessionsToDisplay}
                      onSendEmail={handleSendEmailToUser}
                      onSendGroupEmail={handleSendGroupEmail}
                      onDelete={(id) => apiDelete('invitations', id)}
                      sessionRegistrationCounts={filteredCountsForList}
                      countries={countries}
                      cities={cities}
                    />
                  </div>
                );
              case 'stats':
                return <InvitationStats />;
              case 'history':
                const pastRegisteredUsers = allTransformedUsers.filter(user => {
                  if (!user.sessions || user.sessions.length === 0) return false;
                  return user.sessions.every(session => {
                    return session.start_time && new Date(session.start_time) < todayStart;
                  });
                });
                return (
                  <div>
                    <h2>Historique des Inscriptions</h2>
                    <button 
                      onClick={() => setShowTargetedEmailModal(true)}
                      className="action-btn btn-email" 
                      style={{ marginBottom: '20px', padding: '10px 20px', fontSize: '1em' }}
                    >
                      Envoyer Email Ciblé
                    </button>
                    <RegistrationHistory
                      users={pastRegisteredUsers}
                      onDelete={(id) => apiDelete('invitations', id)}
                      sessionRegistrationCounts={sessionRegistrationCounts}
                      countries={countries}
                      cities={cities}
                      sessions={sessions}
                    />
                  </div>
                );
              case 'sessionHistory':
                return (
                  <div>
                    <SessionHistoryTable
                      sessions={completedSessions}
                      sessionRegistrationCounts={sessionRegistrationCounts}
                      countries={countries}
                      cities={cities}
                      completedSessionsCount={completedSessionsCount}
                      allUsers={allTransformedUsers}
                    />
                  </div>
                );
              default:
                return null;
            }
          })()}
        </div>
      </div>
    );
  }
  const renderMainContent = () => {
    switch (activeView) {
      case 'inscription':
        return renderInscriptionContent();
      case 'invitation':
        return renderInvitationSection();
      default:
        return <div>Contenu non trouvé</div>;
    }
  };

  return (
    <div className="dashboard-layout">
      <aside className="sidebar">
        <div className='side'>
          <h1 className="sidebar-title">Admin</h1>
          <nav className="sidebar-nav">
            <ul>
              <li onClick={() => setActiveView('invitation')} className={activeView === 'invitation' ? 'active' : ''}><FaEnvelopeOpenText />Formation</li>
              <li onClick={() => setActiveView('inscription')} className={activeView === 'inscription' ? 'active' : ''}><FaUsers />Inscription</li>
              <li onClick={handleLogout}><FaSignOutAlt /> Déconnexion</li>
            </ul>
          </nav>
        </div>
      </aside>
      <main className="main-content">
        {renderMainContent()}
        <EmailModal
            isOpen={showEmailModal}
            onClose={() => setShowEmailModal(false)}
            onSend={handleSendGeneralEmail}
            ambassadorEmailLists={ambassadorEmailLists}
            initialRecipients={emailRecipients}
        />

        <EditSessionModal
            isOpen={showEditSessionModal}
            onClose={() => setShowEditSessionModal(false)}
            session={sessionToEdit}
            onSave={handleSaveEditedSession}
            countries={countries}
            cities={cities}
        />

        {showTargetedEmailModal && (
            <div className="modal-overlay">
                <div className="modal-content">
                    <h2>Envoyer Email Ciblé</h2>
                    <button onClick={() => setShowTargetedEmailModal(false)} className="modal-close-button"><FaTimes /></button>
                    <div className="form-group">
                        <label>Ville:</label>
                        <select
                            value={selectedCityForEmail}
                            onChange={(e) => {
                                setSelectedCityForEmail(e.target.value);
                                setSelectedSessionTitleForEmail('');
                            }}
                        >
                            <option value="">Sélectionner une ville</option>
                            {cities.map(city => (
                                <option key={city.id} value={city.id}>{city.name.fr}</option>
                            ))}
                        </select>
                    </div>
                    {selectedCityForEmail && (
                        <div className="form-group">
                            <label>Titre de Session:</label>
                            <select
                                value={selectedSessionTitleForEmail}
                                onChange={(e) => setSelectedSessionTitleForEmail(e.target.value)}
                            >
                                <option value="">Sélectionner un titre de session</option>
                                {sessions
                                    .filter(s => s.cityId === selectedCityForEmail)
                                    .map(s => s.title)
                                    .filter((value, index, self) => self.indexOf(value) === index)
                                    .map(title => (
                                        <option key={title} value={title}>{title}</option>
                                    ))}
                            </select>
                        </div>
                    )}
                    <div className="form-group">
                        <label>Sujet:</label>
                        <input type="text" value={emailRecipients.subject} onChange={(e) => setEmailRecipients({ ...emailRecipients, subject: e.target.value })} />
                    </div>
                    <div className="form-group">
                        <label>Message:</label>
                        <textarea value={emailRecipients.body} onChange={(e) => setEmailRecipients({ ...emailRecipients, body: e.target.value })} rows="8"></textarea>
                    </div>
                    <button 
                        onClick={() => {
                            const recipients = allTransformedUsers.filter(user => {
                                const userCityId = cities.find(c => c.name.fr === user.cityName || c.name.en === user.cityName)?.id;
                                const userSessionTitles = user.sessions.map(s => s.title);
                                const cityMatch = selectedCityForEmail ? userCityId === selectedCityForEmail : true;
                                const sessionMatch = selectedSessionTitleForEmail ? userSessionTitles.includes(selectedSessionTitleForEmail) : true;
                                return cityMatch && sessionMatch;
                            }).map(user => user.email);

                            if (recipients.length > 0) {
                                handleSendGeneralEmail(emailRecipients.subject, emailRecipients.body, recipients);
                            } else {
                                openAlertDialog('Aucun utilisateur trouvé pour les critères sélectionnés.', 'Aucun destinataire');
                            }
                            setShowTargetedEmailModal(false);
                            setSelectedCityForEmail('');
                            setSelectedSessionTitleForEmail('');
                            setEmailRecipients({});
                        }}
                        className="action-btn btn-email"
                    >
                        Envoyer
                    </button>
                </div>
            </div>
        )}
      </main>
    </div>
  );
};
  
export default Dashboard;